import { isFunction } from '@/helpers/misc';
import { computed } from 'vue';
import useFileUpload from './useFileUpload';

const useFileUploadManager = ({
  method,
  multiple,
  extensions,
  validateFile,
  transform,
  onCompleted,
  model,
  uploadUrl,
  defaultValue = null,
  fileNameSuffix,
}) => {
  const { upload, cancelAll: cancelAllUploads } = useFileUpload({ uploadUrl, method });

  const uploadFiles = async (selectedFiles, convert = true) => {
    const files = (Array.from(selectedFiles) || []);

    const onUploadCompleted = async (result, request) => {
      const transformedFile = isFunction(transform) ? transform(result) : result;

      if (multiple) {
        const index = model.value.findIndex(item => item.id === request.id);
        model.value.splice(index, 1, transformedFile);
      } else {
        model.value = transformedFile;
      }

      if (isFunction(onCompleted)) {
        await onCompleted(transformedFile);
      }
    };

    // if single file upload, cancel previous one.
    if (!multiple) {
      await cancelAllUploads(true);
    }

    const requests = await upload({
      files, onUploadCompleted, extensions, validateFile, convert, fileNameSuffix
    });
    const filtered = requests.filter(request => (request && request.id));

    if (multiple) {
      model.value = (model.value || []).concat(filtered);
    } else if (filtered.length > 0) {
      [model.value] = filtered;
    }
  };

  const isFileUploading = computed(() => {
    if (multiple) {
      return model.value && model.value.some(file => file.isUploading);
    }

    return model.value && !!model.value.isUploading;
  });

  const cancelUpload = requestId => {
    if (multiple) {
      const requestToCancel = model.value.find(request => !!request.progress && request.id === requestId);

      if (requestToCancel) {
        const requestIndex = model.value.indexOf(requestToCancel);
        requestToCancel.cancel();
        model.value.splice(requestIndex, 1);
      }
    } else {
      model.value.cancel();
      model.value = defaultValue;
    }
  };

  const cancelAll = async immediate => {
    if (!isFileUploading.value) {
      return true;
    }

    if (await cancelAllUploads(immediate)) {
      if (multiple) {
        model.value.filter(item => item.isUploading).forEach(item => {
          const index = model.value.indexOf(item);
          model.value.splice(index, 1);
        });
      } else {
        model.value = defaultValue;
      }

      return true;
    }

    return false;
  };

  return {
    uploadFiles,
    isFileUploading,
    cancelUpload,
    cancelAll,
  };
};

export default useFileUploadManager;
